import React, { useState } from 'react';
import { Layout, Menu, Typography, Breadcrumb, Space, Drawer, Grid, Button, Avatar, Popconfirm, Tag } from 'antd';
import {
  HomeOutlined,
  AppstoreOutlined,
  SwapOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CalendarOutlined,
  TagOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  LineChartOutlined,
  MenuOutlined,
  UserOutlined,
  TeamOutlined,
  AuditOutlined,
  LogoutOutlined,
  FileDoneOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';

const { Sider, Header, Content, Footer } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const ROL_COLORS = { root: 'red', admin: 'blue', gerencia: 'gold', rh: 'green', vista: 'default' };
const ROL_LABELS = { root: 'Root', admin: 'Admin', gerencia: 'Gerencia', rh: 'RH', vista: 'Vista' };

const breadcrumbMap = {
  '/':                'Dashboard',
  '/productos':       'Productos',
  '/movimientos':     'Movimientos',
  '/categorias':      'Categorías',
  '/proveedores':     'Proveedores',
  '/ordenes-compra':  'Órdenes de Compra',
  '/facturas':        'Facturas',
  '/kpis-categorias': 'KPIs por Categoría',
  '/tendencias-costos': 'Tendencia de Costos',
  '/usuarios':        'Usuarios',
  '/auditoria':       'Auditoría'
};

function AppLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const isRoot = user?.rol === 'root';
  const isRh = user?.rol === 'rh';

  const currentPath = location.pathname;
  const currentLabel = breadcrumbMap[currentPath] || '';

  const handleMenuClick = ({ key }) => {
    navigate(key);
    if (isMobile) setDrawerOpen(false);
  };

  // El rol RH solo tiene acceso al módulo de Facturas — nada más se muestra en su menú.
  const menuItems = isRh
    ? [
        {
          type: 'group',
          label: 'FACTURACIÓN',
          children: [
            { key: '/facturas', icon: <FileDoneOutlined />, label: 'Facturas' }
          ]
        }
      ]
    : [
        {
          type: 'group',
          label: 'INVENTARIO',
          children: [
            { key: '/',            icon: <HomeOutlined />,       label: 'Dashboard'   },
            { key: '/productos',   icon: <AppstoreOutlined />,   label: 'Productos'   },
            { key: '/movimientos', icon: <SwapOutlined />,       label: 'Movimientos' }
          ]
        },
        {
          type: 'group',
          label: 'ADMINISTRACIÓN',
          children: [
            { key: '/categorias',  icon: <TagOutlined />,  label: 'Categorías'  },
            { key: '/proveedores', icon: <ShopOutlined />, label: 'Proveedores' }
          ]
        },
        {
          type: 'group',
          label: 'COMPRAS',
          children: [
            { key: '/ordenes-compra', icon: <ShoppingCartOutlined />, label: 'Órdenes de Compra' }
          ]
        },
        {
          type: 'group',
          label: 'FACTURACIÓN',
          children: [
            { key: '/facturas', icon: <FileDoneOutlined />, label: 'Facturas' }
          ]
        },
        {
          type: 'group',
          label: 'ANÁLISIS',
          children: [
            { key: '/kpis-categorias', icon: <BarChartOutlined />, label: 'KPIs por Categoría' },
            { key: '/tendencias-costos', icon: <LineChartOutlined />, label: 'Tendencia de Costos' }
          ]
        },
        ...(isRoot ? [{
          type: 'group',
          label: 'SISTEMA',
          children: [
            { key: '/usuarios',  icon: <TeamOutlined />,   label: 'Usuarios'  },
            { key: '/auditoria', icon: <AuditOutlined />,  label: 'Auditoría' }
          ]
        }] : [])
      ];

  const menuContent = (
    <>
      <div className="logo-container">
        {(!isMobile && collapsed) ? (
          <span className="logo-text logo-text-collapsed">📦</span>
        ) : (
          <span className="logo-text">📦 Inventario</span>
        )}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[currentPath]}
        items={menuItems}
        onClick={handleMenuClick}
        style={{ borderRight: 0, marginTop: 8 }}
      />
      {/* User info in sidebar (desktop expanded) */}
      {!isMobile && !collapsed && user && (
        <div style={{
          position: 'absolute',
          bottom: 48,
          left: 0,
          right: 0,
          padding: '12px 16px',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Space>
            <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#1677ff' }} />
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{user.nombre}</div>
              <Tag color={ROL_COLORS[user.rol]} style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px' }}>
                {ROL_LABELS[user.rol]}
              </Tag>
            </div>
          </Space>
        </div>
      )}
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sider */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 200,
            boxShadow: '2px 0 8px rgba(0,0,0,0.15)'
          }}
          width={220}
        >
          {menuContent}
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={240}
          bodyStyle={{ padding: 0, background: '#001529' }}
          headerStyle={{ display: 'none' }}
        >
          {menuContent}
        </Drawer>
      )}

      {/* Main area */}
      <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : 220), transition: 'margin-left 0.2s' }}>
        <Header className="site-header">
          <Space align="center">
            {isMobile ? (
              <MenuOutlined
                onClick={() => setDrawerOpen(true)}
                style={{ fontSize: 18, cursor: 'pointer', color: '#1677ff' }}
              />
            ) : collapsed ? (
              <MenuUnfoldOutlined
                onClick={() => setCollapsed(false)}
                style={{ fontSize: 18, cursor: 'pointer', color: '#1677ff' }}
              />
            ) : (
              <MenuFoldOutlined
                onClick={() => setCollapsed(true)}
                style={{ fontSize: 18, cursor: 'pointer', color: '#1677ff' }}
              />
            )}
            <Breadcrumb
              items={[{ title: <HomeOutlined /> }, { title: currentLabel }]}
              style={{ marginLeft: 12 }}
            />
          </Space>

          <Space align="center" className="no-print">
            {screens.sm && (
              <>
                <CalendarOutlined style={{ color: '#1677ff' }} />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {screens.lg
                    ? dayjs().format('dddd, D [de] MMMM [de] YYYY')
                    : dayjs().format('DD/MM/YYYY')}
                </Text>
              </>
            )}
            {user && (
              <Space size={6}>
                <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#1677ff' }} />
                {screens.sm && (
                  <Text style={{ fontSize: 13 }}>{user.nombre}</Text>
                )}
                <Tag color={ROL_COLORS[user.rol]} style={{ fontSize: 11 }}>
                  {ROL_LABELS[user.rol]}
                </Tag>
                <Popconfirm
                  title="¿Cerrar sesión?"
                  description="Se cerrará tu sesión actual."
                  onConfirm={logout}
                  okText="Sí, salir"
                  cancelText="Cancelar"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<LogoutOutlined />}
                    style={{ color: '#ff4d4f' }}
                    title="Cerrar sesión"
                  />
                </Popconfirm>
              </Space>
            )}
          </Space>
        </Header>

        <Content className="site-layout-content">
          {children}
        </Content>

        <Footer className="site-footer">
          Sistema de Inventario © {dayjs().year()} — RADTRANS.
        </Footer>
      </Layout>
    </Layout>
  );
}

export default AppLayout;
